import { DefaultValues } from 'react-hook-form';
import { array, object, string, z } from 'zod';
import {
  BUSINESS_TRAVEL_METHODOLOGY,
  BusinessTravelMethodology,
  SECR_INCLUSION,
} from './constants';

export const recordSchema = object({
  uuid: string().optional(),
  quantity: z.coerce
    .number({
      required_error: 'Please enter a quantity',
      invalid_type_error: 'Please enter a quantity',
    })
    .min(0, 'Quantity can’t be negative'),
  timePeriod: string({ required_error: 'Please select a time period' }).min(
    1,
    'Please select a time period'
  ),
});

export type RecordValues = z.infer<typeof recordSchema>;

const baseFields = {
  site: string().optional(),
  methodology: z.enum([
    BUSINESS_TRAVEL_METHODOLOGY.fuelUsage,
    BUSINESS_TRAVEL_METHODOLOGY.passengerTransport,
    BUSINESS_TRAVEL_METHODOLOGY.totalEmissions,
  ]),
};

const secrInclusionField = z.enum([
  SECR_INCLUSION.include,
  SECR_INCLUSION.exclude,
]);

export const fuelUsageSchema = object({
  ...baseFields,
  methodology: z.literal(BUSINESS_TRAVEL_METHODOLOGY.fuelUsage),
  includeSecr: secrInclusionField,
  fuel: string({ required_error: 'Please select a fuel type' }).min(1),
  unit: string({ required_error: 'Please select a unit' }).min(1),
  records: array(recordSchema),
});

export const distanceSchema = object({
  ...baseFields,
  methodology: z.literal(BUSINESS_TRAVEL_METHODOLOGY.passengerTransport),
  includeSecr: secrInclusionField,
  vehicleType: string({ required_error: 'Please select a vehicle type' }).min(
    1
  ),
  vehicleSize: string().optional(),
  fuelType: string().optional(),
  passengerTransportMode: string({
    required_error: 'Please select a passenger transport mode',
  }).min(1),
  unit: string({ required_error: 'Please select a unit' }).min(1),
  records: array(recordSchema),
});

export const totalEmissionsSchema = object({
  ...baseFields,
  methodology: z.literal(BUSINESS_TRAVEL_METHODOLOGY.totalEmissions),
  includeSecr: z.literal(SECR_INCLUSION.notApplicable),
  description: string({ required_error: 'Please provide a description' }).min(
    1,
    'Description is required'
  ),
  emissions: z.coerce.number({
    required_error: 'Please enter an emissions value',
    invalid_type_error: 'Please enter an emissions value',
  }),
});

export const businessTravelFormSchema = z.discriminatedUnion('methodology', [
  fuelUsageSchema,
  distanceSchema,
  totalEmissionsSchema,
]);

export type BusinessTravelFormValues = z.infer<typeof businessTravelFormSchema>;
export type FuelUsageValues = z.infer<typeof fuelUsageSchema>;
export type DistanceValues = z.infer<typeof distanceSchema>;
export type TotalEmissionsValues = z.infer<typeof totalEmissionsSchema>;

export const isMethodologyValue = (
  value: string | undefined
): value is BusinessTravelMethodology =>
  value === BUSINESS_TRAVEL_METHODOLOGY.fuelUsage ||
  value === BUSINESS_TRAVEL_METHODOLOGY.passengerTransport ||
  value === BUSINESS_TRAVEL_METHODOLOGY.totalEmissions;

export const DEFAULT_FORM_VALUES: DefaultValues<BusinessTravelFormValues> = {
  methodology: BUSINESS_TRAVEL_METHODOLOGY.fuelUsage,
  includeSecr: SECR_INCLUSION.exclude,
  records: [],
};
